import moment from 'moment';
import { createSelector } from 'reselect';
import { RootState } from 'redux/schemas';
import { MentoringProgramReflectionSummary, defaultMentoringProgramReflectionSummary } from 'redux/schemas/models/mentoring-program-reflections';

export type TimeTogether = {
  value: number;
  unit: 'months' | 'weeks' | 'days';
};

const computeTimeTogether = (summary: MentoringProgramReflectionSummary): TimeTogether => {
  if (!summary.firstSessionDate || !summary.lastSessionDate) {
    return { value: 0, unit: 'days' };
  }

  const start = moment(summary.firstSessionDate);
  const end = moment(summary.lastSessionDate);

  const months = end.diff(start, 'months', true);
  if (months >= 1) return { value: Math.round(months), unit: 'months' };

  const weeks = Math.round(end.diff(start, 'weeks', true));
  if (weeks >= 1) return { value: weeks, unit: 'weeks' };

  return { value: Math.max(end.diff(start, 'days'), 1), unit: 'days' };
};

export const getMentoringProgramReflectionStats = createSelector(
  (state: RootState, connectionId: number) => (
    state.app.mentoringProgram.reflections[connectionId]?.summary ?? defaultMentoringProgramReflectionSummary
  ),
  (summary) => ({
    timeTogether: computeTimeTogether(summary),
    totalSessions: summary.pastSessionsCount,
    totalHours: Math.round(summary.pastSessionsDurationSum / 60),
  }),
);

export const getMentoringProgramReflectionSummaryIsLoading = (state: RootState, connectionId: number) => (
  state.app.mentoringProgram.reflections[connectionId]?.isLoading ?? false
);

export const selectMentoringProgramReflectionQuestions = (state: RootState, connectionId: number) => (
  state.app.mentoringProgram.reflections[connectionId]?.questions ?? null
);

export const selectMentoringProgramReflectionParticipantResponses = (state: RootState, connectionId: number) => (
  state.app.mentoringProgram.reflections[connectionId]?.participantResponses ?? null
);

export const selectMentoringProgramReflectionIsSubmitting = (state: RootState, connectionId: number) => (
  state.app.mentoringProgram.reflections[connectionId]?.isSubmitting ?? false
);
